import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { RSVP_RESPONSES, type GuestFormOptions, type GuestListOption } from '@peles/shared';
import { FilterSelect } from '../../common/FilterSelect';
import { SegmentedControl } from '../../common/SegmentedControl';
import type { GuestGroupBy, GuestListState } from './useGuestListParams';

export interface GuestFilterBarProps {
  state: GuestListState;
  options: GuestFormOptions | undefined;
  onChange: (patch: Partial<GuestListState>) => void;
  onClear: () => void;
  hasFilters: boolean;
  /** The office sees where a guest came from; a client does not. */
  showSource: boolean;
}

const SEATED: GuestListOption[] = [
  { value: 'yes', label: 'Seated' },
  { value: 'no', label: 'Not seated' },
];

const SOURCES: GuestListOption[] = [
  { value: 'PRODUCER', label: 'Added by the office' },
  { value: 'CLIENT', label: 'Added by the client' },
  { value: 'IMPORT', label: 'Imported' },
  { value: 'PUBLIC_LINK', label: 'Signed up by link' },
];

const SORTS: GuestListOption[] = [
  { value: 'name', label: 'Last name' },
  { value: 'createdAt', label: 'Date added' },
  { value: 'table', label: 'Table' },
];

const GROUPS: Array<{ id: GuestGroupBy; label: string }> = [
  { id: 'none', label: 'None' },
  { id: 'side', label: 'Side' },
  { id: 'familyGroup', label: 'Family' },
  { id: 'table', label: 'Table' },
  { id: 'response', label: 'Answer' },
];

const byValue = (o: GuestListOption) => o.value;
const byLabel = (o: GuestListOption) => o.label;

/**
 * Search, filters, grouping and sort for the guest list. Every choice goes
 * into the address; the search is sent a moment after typing stops so the
 * list is not asked again for every letter.
 */
export function GuestFilterBar({
  state,
  options,
  onChange,
  onClear,
  hasFilters,
  showSource,
}: GuestFilterBarProps) {
  const [search, setSearch] = useState(state.q);
  // The address may change under us (a stat tile, back button): follow it.
  useEffect(() => setSearch(state.q), [state.q]);
  useEffect(() => {
    if (search === state.q) return;
    const timer = setTimeout(() => onChange({ q: search }), 300);
    return () => clearTimeout(timer);
  }, [search, state.q, onChange]);

  const families = (options?.familyGroups ?? []).map((g) => ({ value: g, label: g }));
  const rsvp = RSVP_RESPONSES.map((o) => ({ value: o.value, label: o.label }));

  return (
    <div className="space-y-3" data-testid="guest-filters">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone"
            aria-label="Search guests"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="RSVP"
          items={rsvp}
          value={state.response}
          onChange={(v) => onChange({ response: v as GuestListState['response'] })}
          getValue={byValue}
          getLabel={byLabel}
          multiple
          searchable={false}
        />
        {(options?.sides.length ?? 0) > 0 && (
          <FilterSelect
            label="Side"
            items={options?.sides ?? []}
            value={state.side}
            onChange={(v) => onChange({ side: v as string[] })}
            getValue={byValue}
            getLabel={byLabel}
            multiple
            searchable={false}
          />
        )}
        {(options?.tags.length ?? 0) > 0 && (
          <FilterSelect
            label="Tags"
            items={options?.tags ?? []}
            value={state.tags}
            onChange={(v) => onChange({ tags: v as string[] })}
            getValue={byValue}
            getLabel={byLabel}
            multiple
          />
        )}
        {families.length > 0 && (
          <FilterSelect
            label="Family"
            items={families}
            value={state.familyGroup}
            onChange={(v) => onChange({ familyGroup: v as string[] })}
            getValue={byValue}
            getLabel={byLabel}
            multiple
          />
        )}
        <FilterSelect
          label="Seating"
          items={SEATED}
          value={state.seated || null}
          onChange={(v) =>
            onChange({ seated: (v === state.seated ? '' : v) as GuestListState['seated'] })
          }
          getValue={byValue}
          getLabel={byLabel}
          searchable={false}
        />
        {showSource && (
          <FilterSelect
            label="Added by"
            items={SOURCES}
            value={state.source}
            onChange={(v) => onChange({ source: v as GuestListState['source'] })}
            getValue={byValue}
            getLabel={byLabel}
            multiple
            searchable={false}
          />
        )}
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              onClear();
            }}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            <X size={14} aria-hidden />
            Clear filters
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span>Group by</span>
          <SegmentedControl
            ariaLabel="Group guests by"
            options={GROUPS}
            value={state.group}
            onChange={(group) => onChange({ group })}
          />
        </div>
        {state.group === 'none' && (
          <div className="flex items-center gap-2">
            <span>Sort</span>
            <FilterSelect
              label="Sort"
              items={SORTS}
              value={state.sort}
              onChange={(v) => onChange({ sort: v as GuestListState['sort'] })}
              getValue={byValue}
              getLabel={byLabel}
              searchable={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
